/**
 * @file Component for showing the children of a one to many relation of the selected parent entity.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { ListBase } from '../list/list.base';
import { EntityState, EntityAction, EntityType } from 'breeze-client';
import { BreezeService } from '../../core/breeze.service';
/**
 * Component for showing the children of a one to many relation of the selected parent entity.
 */
var RelationBase = /** @class */ (function (_super) {
    tslib_1.__extends(RelationBase, _super);
    /**
     * Constructs a RelationComponent.
     * @param configurationsService Service to get the configuration of this component.
     * @param breezeService Service to load and save entity data.
     * @param dialogService Service for displaying dialogs.
     * @param alertsService Service for publish alerts.
     * @param route The active route.
     */
    function RelationBase(userService, breezeService, dialogService, alertsService, translateService, router, overlayService, localStorageService, route, eventsService, actionService) {
        var _this = _super.call(this, userService, breezeService, dialogService, alertsService, translateService, router, overlayService, localStorageService, eventsService, actionService) || this;
        _this.route = route;
        /**
         * The localization domain
         * @memberOf RelationComponent
         */
        _this.labelDomain = 'MISC';
        /** Configuration of the form to show a related detail entity. */
        _this.childForm = undefined;
        /**
         * Configuration of the form to show a new created related detail entity.
         * @type {ChildComponent}
        * */
        _this.createForm = undefined;
        _this.subRelations = [];
        _this.subForms = [];
        /** Handle to breeze entity change event. */
        _this.subscriptionToAdded = undefined;
        /**
         * Hanle to the master entity in this relation.
         */
        _this.masterEntity = null;
        return _this;
    }
    Object.defineProperty(RelationBase.prototype, "master", {
        /**
         * Gets the entity of the master of the depicted master detail relation.
         * @readonly
         * @type {Entity}
         */
        get: function () {
            return this.masterEntity;
        },
        /**
         * Sets the entity of the master of the depicted master detail relation.
         */
        set: function (value) {
            if (this.masterEntity === value) {
                return;
            }
            this.masterEntity = value;
            if (this.entityType && this.masterEntity) {
                this.current = null;
            }
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationBase.prototype, "editMode", {
        /** Gets the edit mode of the current selected entity. */
        get: function () {
            if (!this.current) {
                return false;
            }
            return !this.createMode;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationBase.prototype, "hasChanges", {
        /**
         * Checks whether or not one of the entities has changed.
         * @readonly
         * @type {boolean}
         */
        get: function () {
            if (!this.items) {
                return false;
            }
            var found = this.items.find(function (entity) {
                var entityState = entity.entityAspect.entityState;
                return entityState === EntityState.Modified;
            });
            return found != null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationBase.prototype, "createMode", {
        /** Gets the create mode of the current selected entity. */
        get: function () {
            if (!this.current) {
                return false;
            }
            var entityState = this.current.entityAspect.entityState;
            if (entityState === EntityState.Detached || entityState === EntityState.Added) {
                return true;
            }
            if (this.relationLists.find(function (item) { return item.createMode; }) != null) {
                return true;
            }
            return false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationBase.prototype, "entityLabel", {
        /**
         * Gets the entity label for this relation.
         * @readonly
         * @type {string} The entity label
         */
        get: function () {
            if (!this.configuration) {
                return '';
            }
            if ((this.configuration.labelKey + '').indexOf('APP.' + this.labelDomain + '.') !== 0) {
                // Dit is waar we eerst vanuit gingen.
                return this.configuration.data.entity;
            }
            var endIndex = this.configuration.labelKey.indexOf('.#PLURAL');
            if (endIndex === -1) {
                // Alleen bij labels waar je ondersteuning van een '.#SINGULAR' en '.#NEW' vorm bij verwacht.
                // Ga we anders doen dan eerst.
                return this.configuration.data.entity;
            }
            // Nu kan je dus een ander label gebruiken dan de entity naam.
            return this.configuration.labelKey.substring(this.labelDomain.length + 5, endIndex);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RelationBase.prototype, "foreignKeyValue", {
        get: function () {
            if (this.masterEntity == null) {
                return null;
            }
            var result = '';
            var values = this.masterEntity.entityAspect.getKey().values;
            values.forEach(function (value) {
                result += '_' + value;
            });
            return result.substring(1);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Is called before the current selected entity changes.
     * Here we extend the logic.
     * @protected
     * @param {Entity} value The new current entity.
     */
    RelationBase.prototype.changeCurrent = function (value) {
        var _this = this;
        if (this.subscriptionToAdded) {
            var aspect = this.current.entityAspect;
            this.breezeService.entityManager.entityChanged.unsubscribe(this.subscriptionToAdded);
            this.subscriptionToAdded = undefined;
            if (!aspect.entityState.isDetached()) {
                aspect.rejectChanges();
            }
        }
        // By calling super.changeCurrent a new entity is selected.
        _super.prototype.changeCurrent.call(this, value);
        if (!value) {
            return;
        }
        if (value.entityAspect.entityState === EntityState.Added) {
            // When this.current are just created then...
            var self_1 = this;
            // We go to listen if we need to do a list update
            this.subscriptionToAdded = value.entityAspect.entityManager.entityChanged.subscribe(function (args) {
                if (args.entityAction === EntityAction.MergeOnSave && _this.current === args.entity) {
                    // this.subscriptionToAdded exists only when this.current is a new added entity
                    // The new added entity is currently being saved by someone self.
                    // So I refresh my data list.
                    args.entity.entityAspect.entityManager.entityChanged.unsubscribe(_this.subscriptionToAdded);
                    _this.subscriptionToAdded = undefined;
                    _this.refresh();
                }
            });
        }
    };
    /**
     * Method to determine wether or not the component is shown.
     * @returns {boolean} False to hide the component otherwise true.
     * @memberOf RelationComponent
     * */
    RelationBase.prototype.canShowComponent = function () {
        if (this.entityType == null || this.master == null) {
            return false;
        }
        return true;
    };
    RelationBase.prototype.notifyCanceled = function () {
        this.cancel();
    };
    /**
     * Method to notify the component of a successful entity deletion.
     * @param result The returned response of a entity deletion.
     */
    RelationBase.prototype.notifyRemoval = function (result) {
        // TODO: Check and update list?
    };
    /**
     * Method to notify this component of a breeze commit event.
     * @param result The result of the commit.
     */
    RelationBase.prototype.notifySavement = function (result) {
        var _this = this;
        if (result == null || this.current == null) {
            return;
        }
        var found = result.entities.find(function (item) { return item === _this.current; });
        if (!found) {
            // Only dispatch when save result is about this relation.
            return;
        }
        // Dispatch the received notify of the FormChildComponent so parent component can react.
        if (!this.overlayMode) {
            // Niet in overlayMode see issue:
            // Service aanpassen via planbord aanwezigheid, verbeteren (8 uur) #3888
            this.itemSaved.emit(result);
        }
        this.current = null;
    };
    /**
     * Deletes a entity.
     * @param item The entity to delete.
     * @param index The location of the entity within the shown list.
     * @returns The deleted entity.
     */
    RelationBase.prototype.deleteItem = function (item, index) {
        if (item === this.current) {
            this.current = null;
        }
        return _super.prototype.deleteItem.call(this, item, index);
    };
    /**
     * Deletes a entity.
     * @param item The entity to delete.
     * @param index The index of the entity within the data collection.
     */
    RelationBase.prototype.deleting = function (item, index) {
        this.deleteItem(item, index);
        this.save(undefined);
    };
    /**
     * Method to set an entity in edit mode.
     * @param item The entity that gets the edit mode.
     */
    RelationBase.prototype.edit = function (item) {
        if (this.current && this.current.entityAspect.hasValidationErrors) {
            // Stop detail subdetail "navigate" when errors issue #1761
            return;
        }
        this.current = this.current !== item ? item : null;
    };
    /**
     * Method to retrieve the requested entities from the backend api.
     * @returns An Observable object that retrieves the requested entities.
     */
    RelationBase.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                if (this.master == null) {
                    // Soms heb je dynamische parameter waarden nodig van de master.
                    // Daarom stellen we de master nu verplicht
                    return [2 /*return*/, null];
                }
                return [2 /*return*/, _super.prototype.refresh.call(this)];
            });
        });
    };
    /**
     * Cancels modifications not yet committed.
     */
    RelationBase.prototype.cancel = function () {
        var entity = this.current;
        if (this.editMode && !entity.entityAspect.entityState.isDetached()) {
            entity.entityAspect.rejectChanges();
        }
        this.current = null;
    };
    /**
     * This method is called when save button of the DetailComponent is clicked.
     * Override this method when you are displaying not from the plannings domain.
     * @param {Event} event The click event.
     * @memberOf RelationComponent
     */
    RelationBase.prototype.formSave = function (event) {
        // Override when needed.
    };
    /**
     * This method is called when delete button of the DetailComponent is clicked.
     * Override this method when you are displaying not from the plannings domain.
     * @param {Event} event The click event.
     * @memberOf RelationComponent
     */
    RelationBase.prototype.formDelete = function (event) {
        // Override when needed.
    };
    /**
     * This method is called when cancel button of the DetailComponent is clicked.
     * Override this method when you are displaying not from the plannings domain.
     * @param {Event} event The click event.
     * @memberOf RelationComponent
     */
    RelationBase.prototype.formCancel = function (event) {
        // Override when needed.
    };
    /**
     * Commits modifications in the entity model.
     * @param event The HTML event argument.
     */
    RelationBase.prototype.save = function (event) {
        if (!this.current) {
            return;
        }
        if (this.subscriptionToAdded) {
            this.breezeService.entityManager.entityChanged.unsubscribe(this.subscriptionToAdded);
            this.subscriptionToAdded = undefined;
        }
        if (this.foreignKeyValue != null) {
            var response = {
                entities: [],
                keyMappings: null,
                XHR: null
            };
            response.entities.push(this.current);
            this.current = null;
            this.refresh();
            this.itemSaved.emit(response);
            return;
        }
        if (!this.breezeService.hasChanges()) {
            this.current = null;
            this.refresh();
            return;
        }
        _super.prototype.save.call(this, event);
    };
    /**
     * Is called when key arrow up or down is used.
     * The main list selects the next item, up or down, in response.
     * The relation list does nothing.
     * @protected
     * @param {number} keyCode Key code of the key event.
     */
    RelationBase.prototype.onKeyUpOrKeyDown = function (keyCode) {
        // Does nothing, because
        // We have no knowledge which list is in focus.
    };
    /**
     * Method to initialize the data set for the first time.
     */
    RelationBase.prototype.initItemSet = function () {
        var _this = this;
        var entityConfig = this.configuration.data;
        if (entityConfig == null) {
            return;
        }
        this.sourceName = entityConfig.dataSource;
        this.queryOptions.parameters = Object.assign({}, entityConfig.parameters);
        this.queryOptions.expand = entityConfig.expand;
        this.queryOptions.take = entityConfig.listSize;
        this.fields = Object.create(entityConfig.fields);
        this.relation = entityConfig.entity;
        BreezeService.getDynamicParams(this.queryOptions, this.dynamicParameters);
        this.entityType = this.breezeService.getEntityType(entityConfig.entity);
        if (this.configuration.components && this.configuration.components.length > 0) {
            var components = Object.create(this.configuration.components);
            this.childForm = components[0];
            this.createForm = this.childForm;
            if (this.configuration.components.length > 1) {
                components.splice(0, 1);
                var test = components[0];
                if (test.component === 'FormChildComponent'
                    && test.configuration.key.lastIndexOf('-new') === test.configuration.key.length - 4) {
                    this.createForm = test;
                    components.splice(0, 1);
                }
                components.forEach(function (component) {
                    if (component.component.indexOf('RelationComponent') !== -1) {
                        _this.subRelations.push(component);
                    }
                    else if (component.component.indexOf('FormComponent') !== -1) {
                        _this.subForms.push(component);
                    }
                });
            }
        }
    };
    /**
     * Is called after a new entity is created.
     * The method triggers the entityCreated event.
     * This method is extended with master detail logic.
     * @protected
     * @param {Entity} item The entity that is created.
    * */
    RelationBase.prototype.afterCreated = function (item) {
        this.breezeService.setupMasterDetail(this.masterType, this.foreignKeyValue, item);
        this.setDefaultValues(item);
        _super.prototype.afterCreated.call(this, item);
    };
    /**
     * Sets domain specific default values.
     * @protected
     * @param {Entity} item The item whose values might be pre-set.
     * @memberOf RelationBase
     */
    RelationBase.prototype.setDefaultValues = function (item) {
    };
    return RelationBase;
}(ListBase));
export { RelationBase };
